"use client";

import { propertyTypeOptions } from "@/info/unit/property-types";
import { cn } from "@/utils/cn";
import { Text } from "react-aria-components";
import type { PropertyType } from "../search/filters/desktop";

interface PropertyTypeOption {
	label: string;
	value: string;
	icon: React.ComponentType<{ className?: string }>;
}

interface PropertyTypeIconGridProps {
	value: PropertyType;
	onChange: (value: string | null) => void;
	required?: boolean;
	description?: string;
	errorMessage?: string;
	options: PropertyTypeOption[];
}

export function PropertyTypeIconGrid({
	value,
	onChange,
	required: _required,
	description,
	errorMessage,
	options = propertyTypeOptions,
}: PropertyTypeIconGridProps) {
	return (
		<div className={cn("group flex flex-col gap-2")}>
			{/* Property Type Grid */}
			<div className="grid grid-cols-4 gap-4" role="group" aria-label="Property type selection">
				{options.map(type => {
					const Icon = type.icon;
					const isSelected = value === type.value;

					return (
						<button
							type="button"
							key={type.value}
							aria-pressed={isSelected}
							onClick={() => {
								// Allow toggle behavior: second click unselects
								const newValue = isSelected ? null : type.value;
								onChange(newValue);
							}}
							className="flex flex-col items-center gap-2 text-center focus:outline-none"
						>
							<div
								aria-hidden="true"
								className={cn(
									"w-14 h-14 rounded-xl border-2 flex items-center justify-center transition-colors cursor-pointer",
									isSelected
										? "border-primary bg-pink-50"
										: "border-gray-300 bg-gray-100 hover:border-gray-400 hover:bg-gray-200",
								)}
							>
								<Icon className={cn("w-7 h-7 transition-colors", isSelected ? "text-primary-600" : "text-body")} />
							</div>
							<span
								className={cn("text-sm font-medium transition-colors", isSelected ? "text-primary-600" : "text-body")}
							>
								{type.label}
							</span>
						</button>
					);
				})}
			</div>

			{description && (
				<Text slot="description" className="text-body-small">
					{description}
				</Text>
			)}

			<Text slot="errorMessage" className="text-body-small-medium text-destructive h-1">
				{errorMessage || ""}
			</Text>
		</div>
	);
}
